import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from './Toast';
import { PrimaryButton } from './buttons';

interface RegisterProps {
  onSuccess?: () => void;
  showLoginLink?: boolean;
  onShowLogin?: () => void;
}

const Register: React.FC<RegisterProps> = ({ onSuccess, showLoginLink = true, onShowLogin }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { register } = useAuth();
  const { showToast, ToastComponent } = useToast();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim() || !email.trim() || !password.trim() || !confirmPassword.trim()) {
      showToast('Please fill in all fields', 'error');
      return;
    }

    if (password !== confirmPassword) {
      showToast('Passwords do not match', 'error');
      return;
    }

    if (password.length < 6) {
      showToast('Password must be at least 6 characters long', 'error');
      return;
    }

    setIsSubmitting(true);
    
    try {
      await register({ 
        name: name.trim(), 
        email: email.trim(), 
        password 
      });
      
      showToast('Account created! Please verify your email.', 'success');
      navigate('/verify-email');
      onSuccess?.();
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Registration failed', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = name.trim() && email.trim() && password.trim() && confirmPassword.trim() && password === confirmPassword && password.length >= 6;

  return (
    <div className="flex items-center justify-center px-4">
      {ToastComponent}
      <div className="max-w-md w-full bg-[#27272A] border border-[#3F3F46] rounded-2xl p-8 shadow-card">
        <h2 className="text-2xl font-bold text-white mb-8 text-center">Register</h2>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-content-secondary mb-2">
              Full Name *
            </label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input-field w-full"
              placeholder="Enter your full name"
              required
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-content-secondary mb-2">
              Email *
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-field w-full"
              placeholder="Enter your email"
              required
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-content-secondary mb-2">
              Password *
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-field w-full"
              placeholder="Enter your password (min 6 characters)"
              required
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-content-secondary mb-2">
              Confirm Password *
            </label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="input-field w-full"
              placeholder="Confirm your password"
              required
              disabled={isSubmitting}
            />
            {confirmPassword && password !== confirmPassword && (
              <p className="text-state-error text-sm mt-1">Passwords do not match</p>
            )}
          </div>

          <PrimaryButton
            type="submit"
            disabled={isSubmitting || !canSubmit}
            className="w-full"
          >
            {isSubmitting ? 'Creating Account...' : 'Create Account'}
          </PrimaryButton>
        </form>

        {showLoginLink && (
          <div className="mt-6 pt-6 border-t border-[#3F3F46] text-center">
            <p className="text-[#9CA3AF]">
              Already have an account?{' '}
              <Link
                to="/login"
                className="text-[#14B8A6] hover:text-[#0D9488] font-medium transition-colors"
              >
                Login here
              </Link>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Register;
